import { type Table } from '@tanstack/react-table';

/**
 * Generate CSV content from table data
 */
export function generateCSV<T>(table: Table<T>): string {
  const headers = table
    .getVisibleFlatColumns()
    .filter((col) => col.id !== 'select' && col.id !== 'actions' && col.id !== 'expand')
    .map((col) => {
      const header = col.columnDef.header;
      if (typeof header === 'string') return header;
      return col.id;
    });

  const rows = table.getFilteredRowModel().rows.map((row) => {
    return table
      .getVisibleFlatColumns()
      .filter((col) => col.id !== 'select' && col.id !== 'actions' && col.id !== 'expand')
      .map((col) => {
        const value = row.getValue(col.id);
        if (value == null) return '';
        const str = String(value);
        // Escape CSV special characters
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      });
  });

  const csvHeaders = headers
    .map((h) => {
      const str = String(h);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    })
    .join(',');

  const csvRows = rows.map((row) => row.join(',')).join('\n');

  return `${csvHeaders}\n${csvRows}`;
}

/**
 * Download a string as a file
 */
export function downloadFile(
  content: string,
  filename: string,
  mimeType: string = 'text/csv'
): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

/**
 * Format a column ID into a human-readable title
 */
export function columnIdToTitle(id: string): string {
  return id
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}
